<template>
  <div class="i-bundle-hero">
    <div class="hero">
      <img
        class="hero"
        :src="isSmOrLarger ? ImageCoachingBundle : ImageCoachingBundleMobile"
        alt=""
      />
      <div class="overlay"></div>
    </div>
    <div class="klarna">
      <div class="text">Pay with</div>
      <img :src="IconKlarnaWhite" />
    </div>
    <div class="header">Buy 3 months, Get <strong>1 free</strong></div>
    <div class="sub">
      Land your dream job in
      {{ GlobalFunctions.setToLastDateTimeOfMonth().toLocaleString("default", { month: "long" }) }}'s hiring surge.
      Klarna lets you split your purchase into
      <strong class="no-wrap">4 small installments</strong>
      <strong> with 0% interests over 6 weeks</strong>. No credit checks. No fees.
    </div>
    <div class="benefit-cards">
      <div class="card what-you-get">
        <div class="card-title">
          <img
            :src="IconCircleCheckGreen"
            class="indicator"
          />
          <h3>What You Get</h3>
        </div>
        <ul class="card-list">
          <li>4 months of coaching</li>
          <li>Lower effective monthly price</li>
          <li>Payment flexibility</li>
          <li>No interest / no fees / no credit checks</li>
        </ul>
      </div>

      <section class="card no-surprises">
        <div class="card-title">
          <div class="indicator">💪</div>
          <h3>No Hidden Surprises</h3>
        </div>
        <ul class="card-list">
          <li>No hard credit checks</li>
          <li>No interest charges</li>
          <li>No hidden fees</li>
          <li>No lengthy applications</li>
        </ul>
      </section>
    </div>
    <div class="countdown-container">
      <div>⏰ OFFER ENDS IN:</div>
      <InlineCountdown
        class="countdown"
        :countdown="props.countdown"
      />
    </div>
    <iBaseButton
      v-bind="BUTTON_STYLE"
      @clicked="goToPricing()"
    />
  </div>
</template>

<script setup lang="ts">
import { white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalFunctions } from "@/helpers/globalFunctions"

// ASSETS
import IconKlarnaWhite from "@/assets/icons/icon-klarna-white.svg"
import IconCircleCheckGreen from "@/assets/icons/icon-circle-check-green.svg"
import ImageCoachingBundle from "@/assets/heros/coaching-bundle.avif"
import ImageCoachingBundleMobile from "@/assets/heros/coaching-bundle.avif"

import iBaseButton from "@/components/base/BaseButton.vue"
import InlineCountdown from "@/components/coaching/coachingBundle/InlineCountdown.vue"
import type { CountdownState } from "@/composables/useCountdown"
// PROPS
const props = defineProps<{ countdown: CountdownState | null }>()
// EMITS
const emit = defineEmits(["scroll-to-pricing"])

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)

// CONSTANTS
const BUTTON_STYLE = {
  size: "medium",
  fontWeight: "500",
  width: "fit-content",
  backgroundImage: "linear-gradient(to right, #9810FA, #155DFC)",
  borderRadius: "12px",
  textColor: white,
  text: "Claim Your Free Month Now"
}

// FUNCTIONS

const goToPricing = () => {
  emit("scroll-to-pricing")
}
</script>

<style scoped lang="scss">
.i-bundle-hero {
  --height: 1000px;
  --height-md: 800px;

  @include flex(column, center, center, $gap: 16px);

  width: 100%;
  height: var(--height);
  color: $white;
  padding: 0;

  @media screen and (min-width: $breakpoint-md) {
    @include flex(column, center, center, $gap: 20px);

    height: var(--height-md);
  }

  .hero {
    position: absolute;
    overflow: hidden;
    height: var(--height);
    width: 100vw;
    top: 48px;
    right: 0;
    z-index: -1;
    background-color: unset;

    @media screen and (min-width: $breakpoint-md) {
      height: var(--height-md);
    }

    img.hero {
      width: 100%;
      height: 100%;
      top: 0;
      object-fit: cover;
      object-position: center top;

      @media screen and (min-width: $breakpoint-md) {
        width: 100vw;
        object-fit: cover;
        object-position: right top;
      }
    }

    .overlay {
      position: absolute;
      height: var(--height);
      inset: 0;
      background: rgb(0 0 0 / 65%);
      pointer-events: none;

      @media screen and (min-width: $breakpoint-md) {
        height: var(--height-md);
      }
    }
  }

  .klarna {
    @include flex(row, center, flex-end, $gap: 6px);

    .text {
      @include text-xl;

      line-height: 20px;
    }
  }

  .header {
    @include text-xxl;

    text-align: center;
    max-width: 500px;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;

      max-width: 600px;
    }
  }

  .sub {
    @include text-lg;

    text-align: center;
    max-width: 100%;

    .no-wrap {
      text-wrap: nowrap;
    }

    @media screen and (min-width: $breakpoint-xs) {
      max-width: 75%;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include text-xl;

      max-width: 850px;
    }
  }

  .countdown {
    margin: 4px 0 8px;
  }

  .benefit-cards {
    width: 100%;
    max-width: 900px;
    padding: 0 16px;
    margin-top: 8px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 24px;

    .card {
      --bullet-color: #2abb67;

      background: $white;
      color: $black900;
      border-radius: 20px;
      padding: 24px;
      box-shadow:
        0 10px 15px -3px rgb(0 0 0 / 10%),
        0 4px 6px -4px rgb(0 0 0 / 10%);
      flex: 1 1 0;
      min-width: 280px;
    }

    .card-title {
      @include flex(row, flex-start, center);

      gap: 8px;
      margin-bottom: 12px;

      h3 {
        @include text-xl;

        font-weight: 700;
        color: $black900;
        margin: 0;
      }

      .indicator {
        height: 20px;
        width: 20px;
        line-height: 16px;
        border: 0;
      }
    }

    .card-list {
      list-style: none;
      margin: 0;
      padding: 0 0 0 4px;
      display: flex;
      flex-direction: column;
      gap: 12px;

      li {
        @include text-sm;

        color: $black800;
        position: relative;
        padding-left: 24px;
      }

      li::before {
        content: "";
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--bullet-color);
        position: absolute;
        left: 0;
        top: 0.8em;
        transform: translateY(-50%);
      }
    }

    .what-you-get {
      --bullet-color: #16a34a; // green
    }

    .no-surprises {
      --bullet-color: #ef4444; // red
    }

    @media screen and (max-width: $breakpoint-md) {
      flex-direction: column;
      gap: 16px;

      .card {
        width: 100%;
      }

      .card-title h3 {
        @include text-lg;
      }
    }
  }
}

.countdown-container {
  @include flex(column, center, center);

  font-size: 20px;
  text-align: center;
  gap: 13px;
}
</style>
